<template>
    <b-container class="bv-example-row">
        <!-- Banner -->
        <b-row>
            <b-col>
                <b-alert show variant="light" class="alert alert-custom alert-white alert-shadow fade show gutter-b">
                    <div class="alert-icon">
                        <span class="svg-icon svg-icon-lg">
                            <inline-svg src="media/svg/icons/Tools/Compass.svg" />
                        </span>
                    </div>
                    <div class="alert-text d-flex justify-content-between align-items-center w-100">
                        <span>
                            <strong>Energy Monitor</strong> — Dual-channel power metering via SCT-013 current clamps.
                            Readings are captured on AC mains and converted using the selected voltage.
                        </span>
                        <span class="d-flex align-items-center flex-shrink-0">
                            <SuitchFavourite :device="current_device"/>
                            <b-button class="ml-3" variant="outline-secondary" v-on:click="recalibrate" :disabled="btnCal==true">
                                <b-spinner small v-show="btnCal == true"></b-spinner>
                                Recalibrate
                            </b-button>
                            <b-button class="ml-2" variant="secondary" v-on:click="update" :disabled="btnUpdate==true">
                                <b-spinner small v-show="btnUpdate == true"></b-spinner>
                                Update firmware
                            </b-button>
                        </span>
                    </div>
                </b-alert>
            </b-col>
        </b-row>

        <!-- Controls: Voltage toggle + View mode -->
        <b-row class="mb-4">
            <b-col md="4">
                <b-card class="card-custom card-shadowless gutter-b" no-body>
                    <b-card-body class="p-4">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <span class="text-muted font-size-sm font-weight-bold d-block mb-1">Voltage / phase</span>
                                <b-input-group append="V" size="sm" style="max-width: 120px;">
                                    <b-form-input type="number" v-model.number="voltage" min="80" max="400"></b-form-input>
                                </b-input-group>
                                <small class="text-muted d-block mt-1">line-to-neutral, not 220/330</small>
                            </div>
                            <div>
                                <span class="text-muted font-size-sm font-weight-bold d-block mb-1">Display</span>
                                <b-button-group size="sm">
                                    <b-button :variant="viewMode === 'amps' ? 'warning' : 'outline-warning'" @click="viewMode = 'amps'">Amps</b-button>
                                    <b-button :variant="viewMode === 'watts' ? 'warning' : 'outline-warning'" @click="viewMode = 'watts'">Watts</b-button>
                                </b-button-group>
                            </div>
                        </div>
                        <small class="text-muted d-block mt-2">
                            Power factor: {{ powerFactor }} (typical for motor/cooling loads)
                        </small>
                    </b-card-body>
                </b-card>
            </b-col>

            <!-- Current Readings -->
            <b-col md="4">
                <b-card class="card-custom card-shadowless gutter-b" no-body>
                    <b-card-body class="p-4">
                        <span class="text-muted font-size-sm font-weight-bold d-block mb-2">Live Reading</span>
                        <div class="d-flex justify-content-between">
                            <div class="text-center flex-fill">
                                <span class="font-size-sm text-muted d-block">Line A</span>
                                <span class="font-size-h3 font-weight-bolder" :style="{ color: colorLineA }">
                                    {{ currentReadingA }}
                                </span>
                                <span class="font-size-sm text-muted d-block">{{ unitLabel }}</span>
                            </div>
                            <div class="text-center flex-fill" style="border-left: 1px solid #eee; border-right: 1px solid #eee;">
                                <span class="font-size-sm text-muted d-block">Line B</span>
                                <span class="font-size-h3 font-weight-bolder" :style="{ color: colorLineB }">
                                    {{ currentReadingB }}
                                </span>
                                <span class="font-size-sm text-muted d-block">{{ unitLabel }}</span>
                            </div>
                            <div class="text-center flex-fill">
                                <span class="font-size-sm text-muted d-block">Total</span>
                                <span class="font-size-h3 font-weight-bolder text-dark">
                                    {{ currentReadingTotal }}
                                </span>
                                <span class="font-size-sm text-muted d-block">{{ unitLabel }}</span>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>

            <!-- Energy Summary (kWh) -->
            <b-col md="4">
                <b-card class="card-custom card-shadowless gutter-b" no-body>
                    <b-card-body class="p-4">
                        <span class="text-muted font-size-sm font-weight-bold d-block mb-2">
                            Energy ({{ rangeLabel }})
                        </span>
                        <div class="d-flex justify-content-between">
                            <div class="text-center flex-fill">
                                <span class="font-size-sm text-muted d-block">Line A</span>
                                <span class="font-size-h4 font-weight-bolder" :style="{ color: colorLineA }">{{ kwhA }}</span>
                                <span class="font-size-sm text-muted d-block">kWh</span>
                            </div>
                            <div class="text-center flex-fill" style="border-left: 1px solid #eee; border-right: 1px solid #eee;">
                                <span class="font-size-sm text-muted d-block">Line B</span>
                                <span class="font-size-h4 font-weight-bolder" :style="{ color: colorLineB }">{{ kwhB }}</span>
                                <span class="font-size-sm text-muted d-block">kWh</span>
                            </div>
                            <div class="text-center flex-fill">
                                <span class="font-size-sm text-muted d-block">Total</span>
                                <span class="font-size-h4 font-weight-bolder text-dark">{{ kwhTotal }}</span>
                                <span class="font-size-sm text-muted d-block">kWh</span>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <!-- Chart -->
        <b-row>
            <b-col>
                <b-card header-tag="header" footer-tag="footer" no-body>
                    <template v-slot:header>
                        <h6 class="mb-0">Power Monitoring
                            <b-badge v-if="lastTimeMessage" variant="light">{{ lastTimeMessage }}</b-badge>
                        </h6>
                    </template>
                    <b-card-body style="flex: none;">
                        <div class="card card-custom bg-gray-100 gutter-b card-shadowless">
                            <div class="card-header h-auto border-0">
                                <div class="card-title py-5">
                                    <h3 class="card-label">
                                        <span class="d-block text-dark font-weight-bolder">{{ chartTitle }}</span>
                                        <span class="d-block text-dark-50 mt-2 font-size-sm">Real-time energy monitoring</span>
                                    </h3>
                                </div>
                                <div class="card-toolbar">
                                    <!-- Series toggles -->
                                    <div class="d-inline-flex align-items-center mr-4">
                                        <b-form-checkbox v-model="showLineA" switch size="sm" class="mr-2">
                                            <span :style="{ color: colorLineA, fontWeight: 'bold' }">Line A</span>
                                        </b-form-checkbox>
                                        <b-form-checkbox v-model="showLineB" switch size="sm" class="mr-2">
                                            <span :style="{ color: colorLineB, fontWeight: 'bold' }">Line B</span>
                                        </b-form-checkbox>
                                        <b-form-checkbox v-model="showTotal" switch size="sm">
                                            <span class="font-weight-bold text-dark">Total</span>
                                        </b-form-checkbox>
                                    </div>
                                    <!-- Time range buttons -->
                                    <ul class="nav nav-pills nav-pills-sm nav-dark-75" role="tablist">
                                        <li class="nav-item">
                                            <b-button :variant="timeRange === 'general' ? 'danger' : ''" @click="switchRange('general')" :disabled="loading">
                                                <b-spinner small v-show="loading && timeRange === 'general'"></b-spinner>
                                                Latest
                                            </b-button>
                                        </li>
                                        <li class="nav-item">
                                            <b-button :variant="timeRange === 'month' ? 'danger' : ''" @click="switchRange('month')" :disabled="loading">
                                                <b-spinner small v-show="loading && timeRange === 'month'"></b-spinner>
                                                Month
                                            </b-button>
                                        </li>
                                        <li class="nav-item">
                                            <b-button :variant="timeRange === 'week' ? 'danger' : ''" @click="switchRange('week')" :disabled="loading">
                                                <b-spinner small v-show="loading && timeRange === 'week'"></b-spinner>
                                                Week
                                            </b-button>
                                        </li>
                                        <li class="nav-item">
                                            <b-button :variant="timeRange === 'day' ? 'danger' : ''" @click="switchRange('day')" :disabled="loading">
                                                <b-spinner small v-show="loading && timeRange === 'day'"></b-spinner>
                                                24 Hour
                                            </b-button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="card-body">
                                <apexchart
                                    v-if="chartSeries.length > 0"
                                    ref="apexLine"
                                    class="card-rounded-bottom"
                                    type="line"
                                    :options="chartOptions"
                                    :series="chartSeries"
                                    height="280"
                                ></apexchart>
                                <div v-else class="text-center py-10 text-muted">
                                    <b-spinner v-if="loading" variant="primary"></b-spinner>
                                    <span v-else>No data available</span>
                                </div>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <!-- ═══════════════════════════════════════════════════ -->
        <!-- PRICE CALCULATOR (CFE Tarifa 1C)                   -->
        <!-- ═══════════════════════════════════════════════════ -->
        <b-row>
            <b-col>
                <b-card class="card-custom card-shadowless gutter-b" no-body>
                    <b-card-body class="p-5">
                        <div class="d-flex align-items-center justify-content-between mb-4">
                            <div>
                                <h5 class="font-weight-bolder mb-1">
                                    <i class="flaticon2-chart text-warning mr-2"></i>Cost Estimate — CFE Tarifa 1C
                                </h5>
                                <span class="text-muted font-size-sm">
                                    Tiered pricing with IVA ({{ (ivaRate * 100).toFixed(0) }}%). Bimestral billing period = 60 days.
                                </span>
                            </div>
                            <b-button size="sm" variant="outline-primary" @click="resetTiersToDefault">
                                Reset to defaults
                            </b-button>
                        </div>

                        <!-- Tier editor table -->
                        <b-table-simple small bordered responsive class="mb-4">
                            <b-thead head-variant="light">
                                <b-tr>
                                    <b-th style="width:30%">Tier</b-th>
                                    <b-th style="width:25%">Up to (kWh)</b-th>
                                    <b-th style="width:25%">$/kWh (MXN)</b-th>
                                    <b-th style="width:20%"></b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr v-for="(tier, idx) in priceTiers" :key="'tier-'+idx">
                                    <b-td>
                                        <b-form-input size="sm" v-model="tier.name" placeholder="Tier name"></b-form-input>
                                    </b-td>
                                    <b-td>
                                        <b-form-input size="sm" type="number" v-model.number="tier.upToKwh" :placeholder="idx === priceTiers.length - 1 ? '∞' : ''" :disabled="idx === priceTiers.length - 1"></b-form-input>
                                    </b-td>
                                    <b-td>
                                        <b-form-input size="sm" type="number" step="0.01" v-model.number="tier.pricePerKwh"></b-form-input>
                                    </b-td>
                                    <b-td class="text-center">
                                        <b-button v-if="priceTiers.length > 1" size="sm" variant="outline-danger" @click="priceTiers.splice(idx, 1)">
                                            <i class="flaticon2-trash"></i>
                                        </b-button>
                                    </b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>
                        <b-button size="sm" variant="outline-success" @click="addTier" class="mb-4">
                            + Add tier
                        </b-button>

                        <!-- Price results -->
                        <b-row>
                            <b-col md="6">
                                <div class="bg-light-warning rounded p-4 mb-3">
                                    <span class="text-muted font-size-sm d-block mb-1">
                                        Visible data ({{ rangeLabel }}) — {{ dataSpanDays }} days
                                    </span>
                                    <span class="font-size-h3 font-weight-bolder text-warning">
                                        ${{ priceVisible.total }} MXN
                                    </span>
                                    <span class="text-muted font-size-sm d-block mt-1">
                                        {{ kwhTotal !== '—' ? kwhTotal : '0' }} kWh · subtotal ${{ priceVisible.subtotal }} + IVA ${{ priceVisible.iva }}
                                    </span>
                                    <div class="mt-2" v-if="priceVisible.breakdown.length > 0">
                                        <small v-for="(b, i) in priceVisible.breakdown" :key="'bv-'+i" class="d-block text-muted">
                                            {{ b.name }}: {{ b.kwh }} kWh × ${{ b.rate }}/kWh = ${{ b.cost }}
                                        </small>
                                    </div>
                                </div>
                            </b-col>
                            <b-col md="6">
                                <div class="bg-light-danger rounded p-4 mb-3">
                                    <span class="text-muted font-size-sm d-block mb-1">
                                        Projected bimestral (60 days)
                                    </span>
                                    <span class="font-size-h3 font-weight-bolder text-danger">
                                        ${{ priceBimestral.total }} MXN
                                    </span>
                                    <span class="text-muted font-size-sm d-block mt-1">
                                        ~{{ priceBimestral.projectedKwh }} kWh · subtotal ${{ priceBimestral.subtotal }} + IVA ${{ priceBimestral.iva }}
                                    </span>
                                    <div class="mt-2" v-if="priceBimestral.breakdown.length > 0">
                                        <small v-for="(b, i) in priceBimestral.breakdown" :key="'bb-'+i" class="d-block text-muted">
                                            {{ b.name }}: {{ b.kwh }} kWh × ${{ b.rate }}/kWh = ${{ b.cost }}
                                        </small>
                                    </div>
                                </div>
                            </b-col>
                        </b-row>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <!-- ═══════════════════════════════════════════════════ -->
        <!-- WHAT-IF SIMULATOR                                   -->
        <!-- ═══════════════════════════════════════════════════ -->
        <b-row>
            <b-col>
                <b-card class="card-custom card-shadowless gutter-b" no-body>
                    <b-card-body class="p-5">
                        <div class="d-flex align-items-center justify-content-between mb-4">
                            <div>
                                <h5 class="font-weight-bolder mb-1">
                                    <i class="flaticon2-layers-1 text-info mr-2"></i>What-If Simulator
                                </h5>
                                <span class="text-muted font-size-sm">
                                    Override consumption for specific time windows to estimate savings.
                                    <template v-if="simRules.length > 0 && simActive">
                                        <b-badge variant="warning" class="ml-2">SIMULATION ACTIVE</b-badge>
                                    </template>
                                </span>
                            </div>
                            <div>
                                <b-button v-if="simRules.length > 0 && simActive" size="sm" variant="danger" @click="simActive = false" class="mr-2">
                                    Reset to real data
                                </b-button>
                                <b-button v-if="simRules.length > 0 && !simActive" size="sm" variant="success" @click="simActive = true">
                                    Apply simulation
                                </b-button>
                            </div>
                        </div>

                        <!-- Rules table -->
                        <b-table-simple small bordered responsive class="mb-3">
                            <b-thead head-variant="light">
                                <b-tr>
                                    <b-th style="width:22%">Days</b-th>
                                    <b-th style="width:14%">From</b-th>
                                    <b-th style="width:14%">To</b-th>
                                    <b-th style="width:18%">Override (W)</b-th>
                                    <b-th style="width:22%">Applies to</b-th>
                                    <b-th style="width:10%"></b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr v-for="(rule, idx) in simRules" :key="'rule-'+idx">
                                    <b-td>
                                        <div class="d-flex flex-wrap" style="gap:2px;">
                                            <b-button v-for="d in dayOptions" :key="d.value+'-'+idx"
                                                :variant="rule.days.includes(d.value) ? 'primary' : 'outline-secondary'"
                                                size="sm" style="padding:2px 6px; font-size:11px;"
                                                @click="toggleDay(rule, d.value)">
                                                {{ d.label }}
                                            </b-button>
                                        </div>
                                    </b-td>
                                    <b-td>
                                        <b-form-input size="sm" type="time" v-model="rule.fromTime"></b-form-input>
                                    </b-td>
                                    <b-td>
                                        <b-form-input size="sm" type="time" v-model="rule.toTime"></b-form-input>
                                    </b-td>
                                    <b-td>
                                        <b-form-input size="sm" type="number" step="1" v-model.number="rule.watts" placeholder="Watts"></b-form-input>
                                    </b-td>
                                    <b-td>
                                        <b-form-select size="sm" v-model="rule.channel" :options="channelOptions"></b-form-select>
                                    </b-td>
                                    <b-td class="text-center">
                                        <b-button size="sm" variant="outline-danger" @click="simRules.splice(idx, 1)">
                                            <i class="flaticon2-trash"></i>
                                        </b-button>
                                    </b-td>
                                </b-tr>
                                <b-tr v-if="simRules.length === 0">
                                    <b-td colspan="6" class="text-center text-muted py-3">
                                        No rules yet. Add a rule to simulate different consumption patterns.
                                    </b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>
                        <b-button size="sm" variant="outline-info" @click="addSimRule" class="mb-4">
                            + Add rule
                        </b-button>

                        <!-- Simulation comparison -->
                        <template v-if="simRules.length > 0 && simActive">
                            <hr>
                            <b-row>
                                <b-col md="4">
                                    <div class="bg-light-primary rounded p-4 text-center">
                                        <span class="text-muted font-size-sm d-block mb-1">Original ({{ rangeLabel }})</span>
                                        <span class="font-size-h4 font-weight-bolder text-primary">${{ priceVisible.total }} MXN</span>
                                        <span class="text-muted font-size-sm d-block">{{ kwhTotal !== '—' ? kwhTotal : '0' }} kWh</span>
                                    </div>
                                </b-col>
                                <b-col md="4">
                                    <div class="bg-light-success rounded p-4 text-center">
                                        <span class="text-muted font-size-sm d-block mb-1">Simulated ({{ rangeLabel }})</span>
                                        <span class="font-size-h4 font-weight-bolder text-success">${{ simPriceVisible.total }} MXN</span>
                                        <span class="text-muted font-size-sm d-block">{{ simKwhTotal }} kWh</span>
                                    </div>
                                </b-col>
                                <b-col md="4">
                                    <div class="rounded p-4 text-center" :class="simSavings >= 0 ? 'bg-light-success' : 'bg-light-danger'">
                                        <span class="text-muted font-size-sm d-block mb-1">Difference</span>
                                        <span class="font-size-h4 font-weight-bolder" :class="simSavings >= 0 ? 'text-success' : 'text-danger'">
                                            {{ simSavings >= 0 ? '-' : '+' }}${{ Math.abs(simSavings).toFixed(2) }} MXN
                                        </span>
                                        <span class="text-muted font-size-sm d-block">
                                            {{ simSavings >= 0 ? 'Savings' : 'Extra cost' }} · {{ simSavingsPercent }}%
                                        </span>
                                    </div>
                                </b-col>
                            </b-row>
                            <b-row class="mt-3">
                                <b-col md="6">
                                    <div class="bg-light-success rounded p-4">
                                        <span class="text-muted font-size-sm d-block mb-1">Projected bimestral (simulated)</span>
                                        <span class="font-size-h4 font-weight-bolder text-success">${{ simPriceBimestral.total }} MXN</span>
                                        <span class="text-muted font-size-sm d-block">~{{ simPriceBimestral.projectedKwh }} kWh</span>
                                    </div>
                                </b-col>
                                <b-col md="6">
                                    <div class="rounded p-4" :class="simSavingsBimestral >= 0 ? 'bg-light-success' : 'bg-light-danger'">
                                        <span class="text-muted font-size-sm d-block mb-1">Bimestral savings</span>
                                        <span class="font-size-h4 font-weight-bolder" :class="simSavingsBimestral >= 0 ? 'text-success' : 'text-danger'">
                                            {{ simSavingsBimestral >= 0 ? '-' : '+' }}${{ Math.abs(simSavingsBimestral).toFixed(2) }} MXN
                                        </span>
                                    </div>
                                </b-col>
                            </b-row>
                            <small class="text-muted d-block mt-3">
                                <i class="flaticon-warning-sign text-warning mr-1"></i>
                                Simulation active — chart shows real data, cost comparison uses overridden values for matched time windows.
                            </small>
                        </template>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <!-- Config row: send sensor model + phases to the device over IoT -->
        <b-row class="mb-4" v-if="current_device">
            <b-col>
                <SuitchEnermonConfigWidget :device="current_device" />
            </b-col>
        </b-row>

        <!-- Details row -->
        <b-row>
            <b-col>
                <SuitchPropsWidget :device="current_device"/>
                <br>
                <SuitchDetailsWidget :device="current_device" />
            </b-col>
            <b-col>
                <SuitchStatusWidget :device="current_device"/>
                <br />
                <br />
                <SuitchLogWidget :device="current_device"/>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import {
    FETCH_SINGLE_DEVICE,
    FETCH_CHART_DATA,
    FETCH_CHART_DATA_24H,
    FETCH_CHART_DATA_7D,
    FETCH_CHART_DATA_1M,
    UPDATE_FIRMWARE,
    BROADCAST_ACTION,
} from "@/core/services/store/devices.module";
import { mapGetters } from "vuex";
import SuitchStatusWidget from "@/view/content/widgets/suitch/suitch-status-widget.vue";
import SuitchPropsWidget from "@/view/content/widgets/suitch/suitch-props-widget.vue";
import SuitchDetailsWidget from "@/view/content/widgets/suitch/suitch-details-widget.vue";
import SuitchFavourite from "@/view/content/widgets/suitch/mark-favourite-widget.vue";
import SuitchLogWidget from "@/view/content/widgets/suitch/suitch-log-widget.vue";
import SuitchEnermonConfigWidget from "@/view/content/widgets/suitch/suitch-enermon-config-widget.vue";

export default {
    data() {
        return {
            current_device: null,
            // firmware update / remote calibration button state
            btnUpdate: false,
            btnCal: false,
            // Controls — per-phase (line-to-neutral) voltage, default 126
            voltage: 126,
            viewMode: 'watts',
            powerFactor: 0.85,
            // Series visibility
            showLineA: true,
            showLineB: true,
            showTotal: true,
            // Chart colors
            colorLineA: '#F64E60',
            colorLineB: '#0BB783',
            colorTotal: '#3699FF',
            // Time range
            timeRange: 'general',
            loading: false,
            // Raw data cache (always in amps) per range
            rawData: {
                general: null,
                day: null,
                week: null,
                month: null,
            },
            // Real-time
            lastTimeLoaded: -1,
            lastTimeMessage: '',
            unsubscribe: null,
            timerInterval: null,
            // IVA
            ivaRate: 0.16,
            // Price tiers (CFE Tarifa 1C — bimestral kWh thresholds)
            priceTiers: [
                { name: 'Básico', upToKwh: 150, pricePerKwh: 0.77 },
                { name: 'Intermedio bajo', upToKwh: 300, pricePerKwh: 0.96 },
                { name: 'Intermedio alto', upToKwh: 450, pricePerKwh: 1.40 },
                { name: 'Excedente', upToKwh: Infinity, pricePerKwh: 3.00 },
            ],
            // Simulator
            simRules: [],
            simActive: false,
            dayOptions: [
                { label: 'Mon', value: 1 },
                { label: 'Tue', value: 2 },
                { label: 'Wed', value: 3 },
                { label: 'Thu', value: 4 },
                { label: 'Fri', value: 5 },
                { label: 'Sat', value: 6 },
                { label: 'Sun', value: 0 },
            ],
            channelOptions: [
                { text: 'Both lines', value: 'both' },
                { text: 'Line A only', value: 'a' },
                { text: 'Line B only', value: 'b' },
            ],
        };
    },

    computed: {
        ...mapGetters(["layoutConfig"]),

        /** Currently active raw dataset (array of {value, created_at}) sorted ascending */
        activeRaw() {
            const raw = this.rawData[this.timeRange];
            if (!raw || !raw.values || raw.values.length === 0) return [];
            return [...raw.values].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        },

        /** Parsed channels: { timestamps[], ampsA[], ampsB[] } */
        parsed() {
            const timestamps = [];
            const ampsA = [];
            const ampsB = [];
            for (const point of this.activeRaw) {
                timestamps.push(this.formatDate(point.created_at));
                const val = String(point.value);
                if (val.indexOf(',') !== -1) {
                    const parts = val.split(',');
                    ampsA.push(Number(parts[0]) || 0);
                    ampsB.push(Number(parts[1]) || 0);
                } else {
                    ampsA.push(Number(val) || 0);
                    ampsB.push(0);
                }
            }
            return { timestamps, ampsA, ampsB };
        },

        /** Converted display values for each channel */
        displayA() {
            return this.convertValues(this.parsed.ampsA);
        },
        displayB() {
            return this.convertValues(this.parsed.ampsB);
        },
        displayTotal() {
            return this.displayA.map((v, i) => +(v + this.displayB[i]).toFixed(2));
        },

        unitLabel() {
            return this.viewMode === 'watts' ? 'W' : 'A';
        },
        chartTitle() {
            return this.viewMode === 'watts'
                ? `Power (Watts) — ${this.voltage}V AC, PF ${this.powerFactor}`
                : 'Current (Amperes)';
        },
        rangeLabel() {
            const labels = { general: 'Latest', day: '24h', week: '7 days', month: '30 days' };
            return labels[this.timeRange] || 'Latest';
        },

        // --- Current (latest) readings ---
        currentReadingA() {
            if (this.displayA.length === 0) return '—';
            return this.displayA[this.displayA.length - 1].toFixed(2);
        },
        currentReadingB() {
            if (this.displayB.length === 0) return '—';
            return this.displayB[this.displayB.length - 1].toFixed(2);
        },
        currentReadingTotal() {
            if (this.displayTotal.length === 0) return '—';
            return this.displayTotal[this.displayTotal.length - 1].toFixed(2);
        },

        // --- kWh calculations (trapezoidal integration over time) ---
        kwhA() {
            return this.calcKwh(this.parsed.ampsA);
        },
        kwhB() {
            return this.calcKwh(this.parsed.ampsB);
        },
        kwhTotal() {
            if (this.kwhA === '—' || this.kwhB === '—') return '—';
            return (parseFloat(this.kwhA) + parseFloat(this.kwhB)).toFixed(3);
        },

        /** Number of days the visible data spans */
        dataSpanDays() {
            const raw = this.activeRaw;
            if (raw.length < 2) return 0;
            const first = new Date(raw[0].created_at).getTime();
            const last = new Date(raw[raw.length - 1].created_at).getTime();
            return Math.max(((last - first) / 86400000), 0.01).toFixed(1);
        },

        /** Apply tiered pricing to a kWh amount (bimestral thresholds) */
        priceVisible() {
            const kwh = this.kwhTotal !== '—' ? parseFloat(this.kwhTotal) : 0;
            return this.calcTieredPrice(kwh);
        },
        priceBimestral() {
            const kwh = this.kwhTotal !== '—' ? parseFloat(this.kwhTotal) : 0;
            const days = parseFloat(this.dataSpanDays) || 1;
            const projectedKwh = (kwh / days) * 60;
            const result = this.calcTieredPrice(projectedKwh);
            result.projectedKwh = projectedKwh.toFixed(1);
            return result;
        },

        // ---- Simulator computeds ----

        /** Simulated kWh (with rules applied) per channel */
        simParsed() {
            if (!this.simActive || this.simRules.length === 0) return this.parsed;
            const raw = this.activeRaw;
            const ampsA = [...this.parsed.ampsA];
            const ampsB = [...this.parsed.ampsB];
            for (let i = 0; i < raw.length; i++) {
                const dt = new Date(raw[i].created_at);
                if (isNaN(dt)) continue;
                const dayOfWeek = dt.getDay(); // 0=Sun
                const minuteOfDay = dt.getHours() * 60 + dt.getMinutes();
                for (const rule of this.simRules) {
                    if (!rule.days.includes(dayOfWeek)) continue;
                    const fromMin = this.timeToMinutes(rule.fromTime);
                    const toMin = this.timeToMinutes(rule.toTime);
                    if (fromMin === null || toMin === null) continue;
                    const inWindow = fromMin <= toMin
                        ? (minuteOfDay >= fromMin && minuteOfDay < toMin)
                        : (minuteOfDay >= fromMin || minuteOfDay < toMin); // spans midnight
                    if (!inWindow) continue;
                    // Convert override watts back to amps
                    const overrideAmps = (rule.watts || 0) / (this.voltage * this.powerFactor);
                    if (rule.channel === 'a' || rule.channel === 'both') ampsA[i] = overrideAmps;
                    if (rule.channel === 'b' || rule.channel === 'both') ampsB[i] = overrideAmps;
                }
            }
            return { timestamps: this.parsed.timestamps, ampsA, ampsB };
        },
        simKwhA() {
            return this.calcKwhFromAmps(this.simParsed.ampsA);
        },
        simKwhB() {
            return this.calcKwhFromAmps(this.simParsed.ampsB);
        },
        simKwhTotal() {
            if (this.simKwhA === '—' || this.simKwhB === '—') return '—';
            return (parseFloat(this.simKwhA) + parseFloat(this.simKwhB)).toFixed(3);
        },
        simPriceVisible() {
            const kwh = this.simKwhTotal !== '—' ? parseFloat(this.simKwhTotal) : 0;
            return this.calcTieredPrice(kwh);
        },
        simPriceBimestral() {
            const kwh = this.simKwhTotal !== '—' ? parseFloat(this.simKwhTotal) : 0;
            const days = parseFloat(this.dataSpanDays) || 1;
            const projectedKwh = (kwh / days) * 60;
            const result = this.calcTieredPrice(projectedKwh);
            result.projectedKwh = projectedKwh.toFixed(1);
            return result;
        },
        simSavings() {
            const orig = parseFloat(this.priceVisible.total) || 0;
            const sim = parseFloat(this.simPriceVisible.total) || 0;
            return +(orig - sim).toFixed(2);
        },
        simSavingsPercent() {
            const orig = parseFloat(this.priceVisible.total) || 0;
            if (orig === 0) return '0';
            return ((this.simSavings / orig) * 100).toFixed(1);
        },
        simSavingsBimestral() {
            const orig = parseFloat(this.priceBimestral.total) || 0;
            const sim = parseFloat(this.simPriceBimestral.total) || 0;
            return +(orig - sim).toFixed(2);
        },

        // --- Chart config ---
        chartSeries() {
            const series = [];
            if (this.showLineA && this.displayA.length > 0) {
                series.push({ name: 'Line A', data: this.displayA });
            }
            if (this.showLineB && this.displayB.length > 0) {
                series.push({ name: 'Line B', data: this.displayB });
            }
            if (this.showTotal && this.displayTotal.length > 0) {
                series.push({ name: 'Total', data: this.displayTotal });
            }
            return series;
        },
        chartOptions() {
            const colors = [];
            if (this.showLineA) colors.push(this.colorLineA);
            if (this.showLineB) colors.push(this.colorLineB);
            if (this.showTotal) colors.push(this.colorTotal);

            const vm = this;
            return {
                chart: {
                    height: 280,
                    type: 'line',
                    zoom: { enabled: true },
                    toolbar: { show: true },
                },
                colors: colors,
                stroke: {
                    width: [3, 3, 2],
                    curve: 'smooth',
                    dashArray: this.showTotal ? [0, 0, 5] : [0, 0],
                },
                xaxis: {
                    categories: this.parsed.timestamps,
                    tickAmount: 10,
                    labels: {
                        rotate: -45,
                        rotateAlways: false,
                        style: { fontSize: '11px' },
                    },
                },
                yaxis: {
                    title: { text: vm.unitLabel },
                    labels: {
                        formatter: function(val) {
                            return val != null ? val.toFixed(1) : '';
                        },
                    },
                    min: 0,
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    custom: function({ series, seriesIndex, dataPointIndex, w }) {
                        seriesIndex;
                        const cats = w.globals.categoryLabels || w.globals.labels || [];
                        const xLabel = cats[dataPointIndex] || '';
                        const isSimulated = vm.simActive && vm.simAffectedIndices[dataPointIndex];
                        let html = '<div class="apexcharts-tooltip-title" style="font-size:12px;">' + xLabel;
                        if (isSimulated) {
                            html += ' <span style="background:#FFA726;color:#fff;padding:1px 6px;border-radius:3px;font-size:10px;margin-left:4px;">⚡ What-If Rule Applied</span>';
                        }
                        html += '</div>';
                        for (let s = 0; s < series.length; s++) {
                            const val = series[s][dataPointIndex];
                            if (val == null) continue;
                            const color = w.globals.colors[s];
                            const name = w.globals.seriesNames[s];
                            html += '<div class="apexcharts-tooltip-series-group apexcharts-active" style="display:flex;padding:4px 8px;">';
                            html += '<span class="apexcharts-tooltip-marker" style="background:' + color + ';"></span>';
                            html += '<div style="font-size:12px;"><span style="font-weight:600;">' + name + ':</span> ' + val.toFixed(2) + ' ' + vm.unitLabel + '</div>';
                            html += '</div>';
                        }
                        if (isSimulated) {
                            html += '<div style="padding:4px 8px;font-size:10px;color:#78909C;border-top:1px solid #eee;">'
                                + '📋 This reading is overridden by a What-If rule. Check the simulator below for details.</div>';
                        }
                        return html;
                    },
                },
                legend: {
                    position: 'top',
                    horizontalAlign: 'left',
                },
                grid: {
                    borderColor: '#e0e0e0',
                    strokeDashArray: 4,
                    yaxis: { lines: { show: true } },
                },
                annotations: {
                    xaxis: this.simAnnotations,
                },
            };
        },

        /** Build xaxis annotation bands for simulator rule matches */
        simAnnotations() {
            if (!this.simActive || this.simRules.length === 0) return [];
            const raw = this.activeRaw;
            const cats = this.parsed.timestamps;
            if (raw.length === 0) return [];

            // Mark which indices are affected
            const affected = new Array(raw.length).fill(false);
            for (let i = 0; i < raw.length; i++) {
                const dt = new Date(raw[i].created_at);
                if (isNaN(dt)) continue;
                const dayOfWeek = dt.getDay();
                const minuteOfDay = dt.getHours() * 60 + dt.getMinutes();
                for (const rule of this.simRules) {
                    if (!rule.days.includes(dayOfWeek)) continue;
                    const fromMin = this.timeToMinutes(rule.fromTime);
                    const toMin = this.timeToMinutes(rule.toTime);
                    if (fromMin === null || toMin === null) continue;
                    const inWindow = fromMin <= toMin
                        ? (minuteOfDay >= fromMin && minuteOfDay < toMin)
                        : (minuteOfDay >= fromMin || minuteOfDay < toMin);
                    if (inWindow) { affected[i] = true; break; }
                }
            }

            // Build a lookup of which rule(s) match each index for labeling
            const ruleMatchPerIndex = new Array(raw.length).fill(null);
            for (let i = 0; i < raw.length; i++) {
                if (!affected[i]) continue;
                const dt = new Date(raw[i].created_at);
                const dayOfWeek = dt.getDay();
                const minuteOfDay = dt.getHours() * 60 + dt.getMinutes();
                for (const rule of this.simRules) {
                    if (!rule.days.includes(dayOfWeek)) continue;
                    const fromMin = this.timeToMinutes(rule.fromTime);
                    const toMin = this.timeToMinutes(rule.toTime);
                    if (fromMin === null || toMin === null) continue;
                    const inWindow = fromMin <= toMin
                        ? (minuteOfDay >= fromMin && minuteOfDay < toMin)
                        : (minuteOfDay >= fromMin || minuteOfDay < toMin);
                    if (inWindow) { ruleMatchPerIndex[i] = rule; break; }
                }
            }

            // Group consecutive affected indices into ranges
            const annotations = [];
            let start = null;
            for (let i = 0; i < affected.length; i++) {
                if (affected[i] && start === null) start = i;
                if ((!affected[i] || i === affected.length - 1) && start !== null) {
                    const end = affected[i] ? i : i - 1;
                    // Build a descriptive label from the first matching rule
                    const rule = ruleMatchPerIndex[start];
                    const ruleLabel = rule
                        ? '⚡ What-If: ' + (rule.watts || 0) + 'W (' + (rule.fromTime || '') + '–' + (rule.toTime || '') + ')'
                        : '⚡ What-If Override';
                    annotations.push({
                        x: cats[start],
                        x2: cats[end],
                        fillColor: '#FFA726',
                        opacity: 0.12,
                        borderColor: '#FFA726',
                        label: {
                            text: ruleLabel,
                            borderColor: 'transparent',
                            style: {
                                color: '#E65100',
                                background: 'rgba(255, 167, 38, 0.15)',
                                fontSize: '10px',
                                fontWeight: 600,
                                padding: { left: 4, right: 4, top: 2, bottom: 2 },
                            },
                            orientation: 'horizontal',
                            position: 'top',
                            offsetY: -4,
                        },
                    });
                    start = null;
                }
            }
            return annotations;
        },

        /** Boolean map of which data point indices are affected by sim rules (for tooltip) */
        simAffectedIndices() {
            if (!this.simActive || this.simRules.length === 0) return {};
            const raw = this.activeRaw;
            const map = {};
            for (let i = 0; i < raw.length; i++) {
                const dt = new Date(raw[i].created_at);
                if (isNaN(dt)) continue;
                const dayOfWeek = dt.getDay();
                const minuteOfDay = dt.getHours() * 60 + dt.getMinutes();
                for (const rule of this.simRules) {
                    if (!rule.days.includes(dayOfWeek)) continue;
                    const fromMin = this.timeToMinutes(rule.fromTime);
                    const toMin = this.timeToMinutes(rule.toTime);
                    if (fromMin === null || toMin === null) continue;
                    const inWindow = fromMin <= toMin
                        ? (minuteOfDay >= fromMin && minuteOfDay < toMin)
                        : (minuteOfDay >= fromMin || minuteOfDay < toMin);
                    if (inWindow) { map[i] = true; break; }
                }
            }
            return map;
        },
    },

    components: {
        SuitchLogWidget,
        SuitchFavourite,
        SuitchPropsWidget,
        SuitchStatusWidget,
        SuitchDetailsWidget,
        SuitchEnermonConfigWidget,
    },

    methods: {
        /** Request a remote firmware update (same flow as the sfir/valve pages). */
        update(e) {
            e.preventDefault();
            this.btnUpdate = true;
            console.log("Trying to update:", this.$route.params.token);
            this.$store
                .dispatch(UPDATE_FIRMWARE, {
                    token: this.$route.params.token,
                })
                .then(() => { console.log("firmware requested"); })
                .catch((error) => { console.error("Error firmware request:", error); })
                .finally(() => { this.btnUpdate = false; });
        },
        /**
         * Remote recalibration. MUST be done with no load on the sensors: the
         * device samples its zero baseline and replies "set_cal <a>,<b>" over the
         * realtime socket. Uses the same BROADCAST_ACTION relay as the valve.
         */
        recalibrate(e) {
            e.preventDefault();
            this.$bvModal
                .msgBoxConfirm(
                    "Make sure there is NO load on the sensors (no current flowing). The device will sample its zero baseline and reply with the calibration values.",
                    {
                        title: "Recalibrate to zero",
                        okTitle: "Recalibrate",
                        cancelTitle: "Cancel",
                        okVariant: "warning",
                    }
                )
                .then((ok) => {
                    if (!ok) return;
                    this.btnCal = true;
                    this.$store
                        .dispatch(BROADCAST_ACTION, {
                            token: this.current_device.token,
                            secret: this.current_device.secret_token,
                            command_data: "set_cal",
                        })
                        .then(() => { console.log("recalibration requested"); })
                        .catch((error) => { console.error("Error recalibration:", error); })
                        .finally(() => { this.btnCal = false; });
                });
        },
        formatDate(dateString) {
            try {
                const date = new Date(dateString);
                if (isNaN(date)) return null;
                return new Intl.DateTimeFormat(undefined, {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                }).format(date);
            } catch (e) {
                return null;
            }
        },

        /** Convert amps array to display values (amps or watts) */
        convertValues(ampsArray) {
            if (this.viewMode === 'watts') {
                return ampsArray.map(a => +(a * this.voltage * this.powerFactor).toFixed(2));
            }
            return ampsArray.map(a => +a.toFixed(4));
        },

        /** Calculate kWh from amps array using trapezoidal integration */
        calcKwh(ampsArray) {
            const raw = this.activeRaw;
            if (raw.length < 2) return '—';
            let energyWh = 0;
            for (let i = 1; i < raw.length; i++) {
                const t0 = new Date(raw[i - 1].created_at).getTime();
                const t1 = new Date(raw[i].created_at).getTime();
                if (isNaN(t0) || isNaN(t1)) continue;
                const dtHours = (t1 - t0) / 3600000; // ms to hours
                const w0 = ampsArray[i - 1] * this.voltage * this.powerFactor;
                const w1 = ampsArray[i] * this.voltage * this.powerFactor;
                energyWh += ((w0 + w1) / 2) * dtHours;
            }
            return (energyWh / 1000).toFixed(3); // Wh -> kWh
        },

        /** Same as calcKwh but takes its own amps array (for simulator) */
        calcKwhFromAmps(ampsArray) {
            const raw = this.activeRaw;
            if (raw.length < 2 || ampsArray.length < 2) return '—';
            let energyWh = 0;
            for (let i = 1; i < raw.length; i++) {
                const t0 = new Date(raw[i - 1].created_at).getTime();
                const t1 = new Date(raw[i].created_at).getTime();
                if (isNaN(t0) || isNaN(t1)) continue;
                const dtHours = (t1 - t0) / 3600000;
                const w0 = ampsArray[i - 1] * this.voltage * this.powerFactor;
                const w1 = ampsArray[i] * this.voltage * this.powerFactor;
                energyWh += ((w0 + w1) / 2) * dtHours;
            }
            return (energyWh / 1000).toFixed(3);
        },

        /** Apply tiered pricing to kWh value */
        calcTieredPrice(totalKwh) {
            const breakdown = [];
            let remaining = totalKwh;
            let prevLimit = 0;
            let subtotal = 0;
            // Sort tiers by upToKwh ascending
            const sorted = [...this.priceTiers].sort((a, b) => (a.upToKwh || Infinity) - (b.upToKwh || Infinity));
            for (const tier of sorted) {
                if (remaining <= 0) break;
                const tierCapacity = (tier.upToKwh === Infinity || tier.upToKwh === null || tier.upToKwh === '')
                    ? remaining
                    : Math.max(tier.upToKwh - prevLimit, 0);
                const kwhInTier = Math.min(remaining, tierCapacity);
                const cost = kwhInTier * (tier.pricePerKwh || 0);
                breakdown.push({
                    name: tier.name || 'Tier',
                    kwh: kwhInTier.toFixed(2),
                    rate: (tier.pricePerKwh || 0).toFixed(2),
                    cost: cost.toFixed(2),
                });
                subtotal += cost;
                remaining -= kwhInTier;
                prevLimit = tier.upToKwh === Infinity ? Infinity : (tier.upToKwh || prevLimit);
            }
            const iva = subtotal * this.ivaRate;
            return {
                subtotal: subtotal.toFixed(2),
                iva: iva.toFixed(2),
                total: (subtotal + iva).toFixed(2),
                breakdown,
            };
        },

        resetTiersToDefault() {
            this.priceTiers = [
                { name: 'Básico', upToKwh: 150, pricePerKwh: 0.77 },
                { name: 'Intermedio bajo', upToKwh: 300, pricePerKwh: 0.96 },
                { name: 'Intermedio alto', upToKwh: 450, pricePerKwh: 1.40 },
                { name: 'Excedente', upToKwh: Infinity, pricePerKwh: 3.00 },
            ];
        },
        addTier() {
            // Insert before the last (Excedente/catch-all) tier
            const lastIdx = this.priceTiers.length - 1;
            const prevLimit = lastIdx > 0 ? (this.priceTiers[lastIdx - 1].upToKwh || 0) : 0;
            this.priceTiers.splice(lastIdx, 0, {
                name: 'New tier',
                upToKwh: prevLimit + 100,
                pricePerKwh: 1.50,
            });
        },

        /** Simulator helpers */
        addSimRule() {
            this.simRules.push({
                days: [1, 2, 3, 4, 5], // Mon-Fri default
                fromTime: '09:00',
                toTime: '18:00',
                watts: 50,
                channel: 'both',
            });
            this.simActive = true;
        },
        toggleDay(rule, dayValue) {
            const idx = rule.days.indexOf(dayValue);
            if (idx === -1) rule.days.push(dayValue);
            else rule.days.splice(idx, 1);
        },
        timeToMinutes(timeStr) {
            if (!timeStr || timeStr.indexOf(':') === -1) return null;
            const [h, m] = timeStr.split(':').map(Number);
            return h * 60 + m;
        },

        switchRange(range) {
            this.timeRange = range;
            this.fetchData(range);
        },

        fetchData(range) {
            if (!this.current_device) return;
            // Use cache if available
            if (this.rawData[range] !== null) return;
            this.loading = true;
            const token = this.current_device.token;
            const prop_id = 'power';
            const actionMap = {
                general: FETCH_CHART_DATA,
                day: FETCH_CHART_DATA_24H,
                week: FETCH_CHART_DATA_7D,
                month: FETCH_CHART_DATA_1M,
            };
            this.$store
                .dispatch(actionMap[range], { device_token: token, prop_id })
                .then((data) => {
                    this.rawData[range] = data;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                });
        },

        setupRealtimeUpdates() {
            this.unsubscribe = this.$store.subscribe((mutation, state) => {
                if (mutation.type !== 'setMessage') return;
                const msg = state.realtime.last_message;
                if (
                    this.current_device &&
                    msg.token === this.current_device.token &&
                    msg.type === 'prop'
                ) {
                    // Push new value into current range's raw data
                    const raw = this.rawData[this.timeRange];
                    if (raw && raw.values) {
                        raw.values.push({ value: msg.value, created_at: new Date().toISOString() });
                        // Trigger reactivity
                        this.$set(this.rawData, this.timeRange, { ...raw });
                    }
                    this.lastTimeLoaded = Date.now();
                }
            });
        },
    },

    mounted() {
        const device_token = this.$route.params.token;
        this.$store
            .dispatch(FETCH_SINGLE_DEVICE, { device_token })
            .then((data) => {
                this.current_device = data;
                this.fetchData('general');
            });

        this.setupRealtimeUpdates();

        this.timerInterval = setInterval(() => {
            if (this.lastTimeLoaded === -1) return;
            const elapsed = Math.ceil((Date.now() - this.lastTimeLoaded) / 1000);
            this.lastTimeMessage = elapsed < 60
                ? elapsed + ' seconds ago'
                : Math.floor(elapsed / 60) + ' minutes ago';
        }, 1000);
    },

    beforeDestroy() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.timerInterval) clearInterval(this.timerInterval);
    },
};
</script>
